import { useState, useEffect } from 'react';

const THEME_STORAGE_KEY = 'theme';

/**
 * LIGHT MODE IS SUSPENDED until a light counterpart to the PAI design system is
 * designed. The app paints its dark grounds unconditionally, so a "light" theme
 * now renders light-palette text (prose code, for one) on dark glass — unreadable.
 *
 * This flag is the single switch that brings it back. While it is false:
 *   - a stored 'light' preference is COERCED to dark, so nobody who used the app
 *     before the redesign is stranded in a broken UI with no control to escape it
 *     (the old default WAS light, and this hook persists on mount — so essentially
 *     every existing session has 'light' written to localStorage);
 *   - `SettingsModal` hides the Appearance toggle (see SHOW_THEME_TOGGLE there).
 *
 * The mechanism itself — the `dark` class on <html>, the localStorage key, the
 * setters — is deliberately left intact so restoring light mode is flipping this
 * back to true.
 */
const LIGHT_MODE_SUPPORTED = false;

const normalize = (value) => {
    if (value !== 'light' && value !== 'dark') return 'dark';
    return LIGHT_MODE_SUPPORTED ? value : 'dark';
};

/**
 * Custom hook for managing dark/light theme
 * - Loads theme from localStorage on mount
 * - Applies/removes the 'dark' class on the document root
 * - Saves theme to localStorage on change
 */
export function useTheme() {
    const [theme, setThemeState] = useState(() => normalize(localStorage.getItem(THEME_STORAGE_KEY)));

    useEffect(() => {
        // Apply or remove 'dark' class on document root
        const root = document.documentElement;
        if (theme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }

        // Save to localStorage
        localStorage.setItem(THEME_STORAGE_KEY, theme);
    }, [theme]);

    const setTheme = (newTheme) => {
        if (newTheme === 'light' || newTheme === 'dark') {
            setThemeState(normalize(newTheme));
        }
    };

    const toggleTheme = () => {
        setThemeState((prev) => normalize(prev === 'light' ? 'dark' : 'light'));
    };

    return {
        theme,
        setTheme,
        toggleTheme,
    };
}
